import { Canvas } from '@react-three/fiber';
import { Scroll, ScrollControls } from '@react-three/drei';
import { useState, useCallback, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './app/queryClient';
import './assets/index.scss';
import Navbar from './components/Navbar';
import { SEO } from './components/seo/SEO';
import { routeFor } from './seo/routes';
import { ScrollSceneController } from './components/scene/ScrollSceneController';
import { ScrollTransitionScene } from './components/scene/ScrollTransitionScene';
import { ScrollSections } from './components/scene/ScrollSections';
import { useScrollToHash } from './components/scene/useScrollToHash';
import { pagesFor, dampingFor, staticCameraFor } from './components/scene/responsive';
import { ScrollLayoutMeasurer } from './components/scene/ScrollLayoutMeasurer';
import {
  homeScrollLayout,
  useScrollLayout,
  pagesFromMeasurement,
} from './components/scene/scrollLayout';
import { useBreakpoint } from './hooks/useBreakpoint';
import { PostprocessingLayer } from './components/scene/PostprocessingLayer';
import { DprSync } from './components/scene/DprSync';
import { ScrollElBridge } from './components/scene/ScrollElBridge';
import { Footer } from './components/Footer';
import { Loader } from './components/Loader';
import { LanguageSwitchOverlay } from './components/LanguageSwitchOverlay';
import { PageTransitionOverlay } from './components/scene/PageTransitionOverlay';
import { RouteFade } from './components/RouteFade';
import { SideNavOverlay } from './components/SideNavOverlay';
import { useContent } from './hooks/useContent';
import { useTranslationsQuery } from './hooks/useTranslationsQuery';
import { ContentError } from './components/ui/ContentError';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { MaintenancePage } from './pages/MaintenancePage';
import { PRODUCT_PAGES, productPath } from './seo/productPages';
import { MuteToggle } from './components/reading/MuteToggle';
import { DeckPaidModal } from './components/shop/DeckPaidModal';
import { BookingPaidModal } from './components/booking/BookingPaidModal';
import { useRouteAnalytics } from './analytics/useRouteAnalytics';
import { useStripePurchase } from './analytics/useStripePurchase';

// Heavy 3D scene routes (each pulls its own GLB models) — split out of the
// initial chunk so landing on "/" doesn't pay for /decks or /radio. They
// render inside the Canvas's own <Suspense>, so the chunk loads on page swap.
const DecksPageContent = lazy(() =>
  import('./components/DecksPageContent').then((m) => ({ default: m.DecksPageContent })),
);
const RadioPageContent = lazy(() =>
  import('./components/RadioPageContent').then((m) => ({ default: m.RadioPageContent })),
);

// Route-level pages are code-split: none of them are needed for the initial
// "/" scene, so they load on navigation instead of bloating the main chunk.
// (All are named exports, hence the .then(m => ({ default })) shape.)
const TermsPage = lazy(() => import('./pages/TermsPage').then((m) => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })));
const ReturnPolicyPage = lazy(() =>
  import('./pages/ReturnPolicyPage').then((m) => ({ default: m.ReturnPolicyPage })),
);
const WarrantyPolicyPage = lazy(() =>
  import('./pages/WarrantyPolicyPage').then((m) => ({ default: m.WarrantyPolicyPage })),
);
const DesignSystemPage = lazy(() =>
  import('./pages/design-system').then((m) => ({ default: m.DesignSystemPage })),
);
const ReadingPage = lazy(() =>
  import('./components/reading/ReadingPage').then((m) => ({ default: m.ReadingPage })),
);
const DailyCardPage = lazy(() =>
  import('./components/daily-card/DailyCardPage').then((m) => ({ default: m.DailyCardPage })),
);
const AllReviewsPage = lazy(() =>
  import('./pages/AllReviewsPage').then((m) => ({ default: m.AllReviewsPage })),
);
const SubscribePage = lazy(() =>
  import('./pages/SubscribePage').then((m) => ({ default: m.SubscribePage })),
);
const VerifyPage = lazy(() =>
  import('./pages/VerifyPage').then((m) => ({ default: m.VerifyPage })),
);
const UserManualsPage = lazy(() =>
  import('./pages/UserManualsPage').then((m) => ({ default: m.UserManualsPage })),
);
const NotFoundPage = lazy(() =>
  import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);
const SpreadLab = lazy(() =>
  import('./pages/lab/spread/SpreadLab').then((m) => ({ default: m.SpreadLab })),
);
const CardsPage = lazy(() => import('./pages/CardsPage').then((m) => ({ default: m.CardsPage })));
const ServicePage = lazy(() =>
  import('./pages/products/ServicePage').then((m) => ({ default: m.ServicePage })),
);
const GuidanceDeckPage = lazy(() =>
  import('./pages/products/GuidanceDeckPage').then((m) => ({ default: m.GuidanceDeckPage })),
);
const MediumshipDeckPage = lazy(() =>
  import('./pages/products/MediumshipDeckPage').then((m) => ({ default: m.MediumshipDeckPage })),
);
const BundleDeckPage = lazy(() =>
  import('./pages/products/BundleDeckPage').then((m) => ({ default: m.BundleDeckPage })),
);

// Leva is a dev-only debug GUI; the lazy import lives behind a NODE_ENV check
// so the whole `leva` package is dead-code-eliminated from the prod bundle.
const Leva =
  process.env.NODE_ENV === 'development'
    ? lazy(() => import('leva').then((m) => ({ default: m.Leva })))
    : null;

const STATIC_ROUTES = [
  '/terms',
  '/terms-and-conditions',
  '/privacy',
  '/privacy-policy',
  '/return-policy',
  '/warranty-policy',
  '/design-system',
  '/reading',
  '/card-of-the-day',
  '/reviews',
  '/subscribe',
  '/verify',
  '/spirit-box-user-manual',
  '/lab/spread',
  '/cards/mediumship',
  '/cards/guidance',
  ...PRODUCT_PAGES.map(productPath),
];

function pageFromPath(pathname) {
  if (pathname === '/decks') return 'decks';
  if (pathname === '/spirit-box') return 'radio';
  return 'main';
}

const PAGE_PATHS = { main: '/', decks: '/decks', radio: '/spirit-box' };

// The three scroll-scene routes. Everything else (static full-page routes and
// unknown 404 paths) is NOT a scene route, so the URL-sync effects skip it and
// an unknown path is never redirected to "/". A scene path can arrive with a
// trailing slash (server 301s); strip it so "/decks/" is still recognized.
const SCENE_PATHS = ['/', '/decks', '/spirit-box'];
function isScenePath(pathname) {
  return SCENE_PATHS.includes(pathname.replace(/\/+$/, '') || '/');
}

// Providers live in the outer shell so AppInner's body (useContent and any
// future query hooks) sits *under* QueryClientProvider — a component can't
// consume context it renders itself.
export default function App() {
  if (process.env.REACT_APP_MAINTENANCE === 'true') {
    return <MaintenancePage />;
  }
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <AppInner />
        </ErrorBoundary>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

function CardsRoute() {
  const { deck } = useParams();
  if (deck !== 'guidance' && deck !== 'mediumship') {
    return <Navigate to="/" replace />;
  }
  return <CardsPage deck={deck} />;
}

function AppInner() {
  useTranslationsQuery();
  const breakpoint = useBreakpoint();
  const [activeScene, setActiveScene] = useState('home');
  const [scrollEl, setScrollEl] = useState(null);
  // GPU-resolved dpr clamp; DprSync reports it, the Canvas dpr prop enforces
  // it (see DprSync.tsx — a floor set only via setDpr gets reset by R3F).
  const [dprRange, setDprRange] = useState([1, 2]);
  const location = useLocation();
  const navigate = useNavigate();

  // Page-level navigation: "main" or "decks"
  const [activePage, setActivePage] = useState(() => pageFromPath(location.pathname));
  const [requestedPage, setRequestedPage] = useState(() => pageFromPath(location.pathname));

  const handleSwap = useCallback(() => {
    setActivePage(requestedPage);
  }, [requestedPage]);

  // Owned host node for ScrollSections' portaled "READ MORE" CTA. drei's
  // <Scroll html> keeps its inner ReactDOM root alive after the home tree
  // unmounts, so without an externally-managed host the portal node would
  // leak onto /decks (page swap) or onto a static route like /reading
  // (where activePage stays 'main' since the URL-sync effects skip
  // STATIC_ROUTES). Gated on both so it exists only while the scroll scene
  // route is actually mounted.
  const onScrollSceneRoute = isScenePath(location.pathname);
  const [mainPortalHost, setMainPortalHost] = useState(null);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (activePage !== 'main' || !onScrollSceneRoute) {
      setMainPortalHost(null);
      return;
    }
    const node = document.createElement('div');
    document.body.appendChild(node);
    setMainPortalHost(node);
    return () => {
      if (node.parentNode) node.parentNode.removeChild(node);
    };
  }, [activePage, onScrollSceneRoute]);

  const handleNavigate = useCallback(
    (page) => {
      if (page === activePage) return;
      setRequestedPage(page);
    },
    [activePage],
  );

  // Sync URL when activePage changes (after FBO transition completes)
  useEffect(() => {
    if (!isScenePath(location.pathname)) return;
    const expectedPath = PAGE_PATHS[activePage] ?? '/';
    if (location.pathname !== expectedPath) {
      navigate(expectedPath, { replace: false });
    }
  }, [activePage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle browser back/forward: snap to new page without FBO transition
  useEffect(() => {
    if (!isScenePath(location.pathname)) return;
    const page = pageFromPath(location.pathname);
    if (page !== activePage) {
      setActivePage(page);
      setRequestedPage(page);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useScrollToHash(activePage === 'main' ? scrollEl : null);

  // pages is measured from the overlay content (footer always lands flush);
  // the per-tier constant is only the pre-measurement fallback.
  const homeLayout = useScrollLayout(homeScrollLayout);
  const homePages = pagesFromMeasurement(homeLayout, pagesFor(breakpoint));

  const contentQuery = useContent();
  useRouteAnalytics();
  useStripePurchase();

  const sceneElement = (
    <div className="relative w-full">
      {Leva && (
        <Suspense fallback={null}>
          <Leva collapsed />
        </Suspense>
      )}
      <Navbar scrollEl={scrollEl} activePage={activePage} onNavigate={handleNavigate} />

      {activePage === 'main' ? (
        <SEO
          path="/"
          title={routeFor('/').title}
          description={routeFor('/').description}
        />
      ) : activePage === 'decks' ? (
        <SEO
          path="/decks"
          title={routeFor('/decks').title}
          description={routeFor('/decks').description}
        />
      ) : (
        <SEO
          path="/spirit-box"
          title={routeFor('/spirit-box').title}
          description={routeFor('/spirit-box').description}
        />
      )}

      {/* Hero title now rendered as 3D <CloudTitle /> inside HomePart */}

      <ErrorBoundary>
      <div className="fixed inset-0">
        <Canvas
          className="h-full w-full"
          dpr={dprRange}
          camera={{ position: staticCameraFor(breakpoint), far: 2000, fov: 75 }}
        >
          <Suspense fallback={null}>
            <DprSync onRange={setDprRange} />
            <PageTransitionOverlay
              requestedPage={requestedPage}
              activePage={activePage}
              onSwap={handleSwap}
            >
              {activePage === 'main' ? (
                <ScrollControls pages={homePages} damping={dampingFor(breakpoint)}>
                  <ScrollElBridge onEl={setScrollEl} />
                  <ScrollLayoutMeasurer store={homeScrollLayout} pages={homePages} />
                  <ScrollSceneController onSceneChange={setActiveScene} />
                  <ScrollTransitionScene activeScene={activeScene} />

                  {/* width:100% is load-bearing: drei mounts this root as a
                      position:absolute div with auto width, so it shrink-to-fits
                      to the widest child's min-content. One overlong unbreakable
                      row (RU footer legal links) then stretched every section
                      past the viewport. Pinning the width clips instead. */}
                  <Scroll html style={{ width: '100%' }}>
                    {/* drei's <Scroll html> mounts its children in a
                        detached ReactDOM root, so neither the outer
                        app provider nor the Canvas tree reaches it.
                        Re-provide the singleton client here for
                        ScrollSections' useReviewsQuery. */}
                    <QueryClientProvider client={queryClient}>
                      {/* Measured by ScrollLayoutMeasurer: total
                          height drives pages, [data-scroll-anchor]
                          offsets drive scene thresholds. */}
                      <div data-scroll-content>
                        <ScrollSections
                          activeScene={activeScene}
                          onNavigate={handleNavigate}
                          onRouteNavigate={navigate}
                          portalHost={mainPortalHost}
                        />
                        <Footer onLegalNavigate={navigate} />
                      </div>
                    </QueryClientProvider>
                  </Scroll>
                </ScrollControls>
              ) : activePage === 'decks' ? (
                <DecksPageContent
                  onEl={setScrollEl}
                  onLegalNavigate={navigate}
                  onNavigate={navigate}
                  isExiting={requestedPage !== activePage}
                />
              ) : (
                <RadioPageContent
                  onEl={setScrollEl}
                  onLegalNavigate={navigate}
                  isExiting={requestedPage !== activePage}
                />
              )}
            </PageTransitionOverlay>
            <PostprocessingLayer breakpoint={breakpoint} />
          </Suspense>
        </Canvas>
      </div>
      </ErrorBoundary>
      <SideNavOverlay
        activePage={activePage}
        scrollEl={scrollEl}
        activeScene={activeScene}
      />
      <Loader />
      <LanguageSwitchOverlay />
      {contentQuery.isError && (
        <ContentError onRetry={() => contentQuery.refetch()} />
      )}
    </div>
  );

  return (
    <>
        <RouteFade staticRoutes={STATIC_ROUTES}>
        <Suspense fallback={<Loader />}>
        <Routes>
          <Route path="/terms-and-conditions" element={<TermsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPage />} />
          <Route path="/return-policy" element={<ReturnPolicyPage />} />
          <Route path="/warranty-policy" element={<WarrantyPolicyPage />} />
          <Route path="/design-system" element={<DesignSystemPage />} />
          <Route path="/reading" element={<ReadingPage />} />
          <Route path="/card-of-the-day" element={<DailyCardPage />} />
          <Route path="/reviews" element={<AllReviewsPage />} />
          <Route path="/subscribe" element={<SubscribePage />} />
          <Route path="/verify" element={<VerifyPage />} />
          {/* Printed as a QR code in every Spirit Box: never rename. */}
          <Route path="/spirit-box-user-manual" element={<UserManualsPage />} />
          <Route path="/lab/spread" element={<SpreadLab />} />
          <Route path="/cards/:deck" element={<CardsRoute />} />

          {PRODUCT_PAGES.map((p) => (
            <Route
              key={p.slug}
              path={productPath(p)}
              element={
                p.kind === 'service' ? (
                  <ServicePage slug={p.slug} serviceKey={p.key} />
                ) : p.kind === 'deck' ? (
                  p.key === 'guidance' ? (
                    <GuidanceDeckPage />
                  ) : (
                    <MediumshipDeckPage />
                  )
                ) : (
                  <BundleDeckPage />
                )
              }
            />
          ))}

          {/* Legacy paths — redirect to canonical (client-side; real 301 requires server config) */}
          <Route path="/terms" element={<Navigate to="/terms-and-conditions" replace />} />
          <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
          {/* Keeps the query: Stripe sessions opened before the rename return here with ?deck_paid=1. */}
          <Route
            path="/radio"
            element={<Navigate to={{ pathname: '/spirit-box', search: location.search, hash: location.hash }} replace />}
          />
          {SCENE_PATHS.map((p) => (
            <Route key={p} path={p} element={sceneElement} />
          ))}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
        </RouteFade>
        <MuteToggle />
        {/* Post-payment confirmation for the deck shop. Mounted at the root (not
            per page) so it works wherever Stripe returns the buyer, and outside
            <Scroll html> so its portal lands in body. */}
        <DeckPaidModal />
        {/* Same pattern for bookings: Calendly's redirect and Stripe's
            success_url land with ?booking_paid=1&service=<key> on whatever
            page the visitor left from. */}
        <BookingPaidModal />
        {process.env.NODE_ENV === 'development' && (
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
        )}
    </>
  );
}
